import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import HarbourFlyout from '../../components/harbour/HarbourFlyout';
import HarbourStepsRail from '../../components/harbour/HarbourStepsRail';
import HarbourButton from '../../components/harbour/HarbourButton';
import HarbourNotice from '../../components/harbour/HarbourNotice';
import HarbourOptionGroup from '../../components/harbour/HarbourOptionGroup';
import HarbourTextLink from '../../components/harbour/HarbourTextLink';
import HarbourModal from '../../components/harbour/HarbourModal';
import HarbourSchoolStaffStatement from '../../components/harbour/HarbourSchoolStaffStatement';
import { HarbourField, HarbourDateField, dateFieldToIso } from '../../components/harbour/HarbourField';
import { apiHarbourStartReferral, apiHarbourRemoveParent, apiHarbourUpdatePupil } from '../../utils/rmsApi';
import { effectiveYearGroup } from '../../utils/harbourPupils';
import { SCHOOL_REFERRAL_CONSENT_VERSION } from '../../constants/consentVersions';
import {
  APPROVED_GP_PRACTICES,
  GP_PRACTICE_OTHER,
  GP_PRACTICE_UNKNOWN,
  evaluateYearGroup,
} from '../../constants/pilotEligibility';
import '../../components/harbour/harbour.css';

const YEAR_GROUPS = ['Reception', ...Array.from({ length: 13 }, (_, i) => `Year ${i + 1}`)];

/*
 * Add to referral pathway (TDI-53 section 14): a four-step wide flyout, then
 * the "Important information" page (4025:112282) the school reads before the
 * referral starts.
 *   1. Consent to refer — the live referral consent question; only "Yes"
 *      proceeds (clinical safety: no referral without consent).
 *   2. Child or Young Person's Details — a read-only summary from the pupil
 *      record (Change links appear on the pupils surfaces while editable).
 *   3. What the referral is for — the live main_reason options; this sets the
 *      intake concern (ADHD / autism / both) and the Reason for Referral form
 *      may refine it later, exactly as in the legacy flow.
 *   4. Informant — nominate the responsible parent or carer; the nominee is
 *      invited by email when the referral starts. The live Request for
 *      Referral question "Have they been told to expect an email" is asked
 *      here too, once: the school's Request for Referral form then arrives
 *      with both answered and never asks again. Only "Yes" proceeds, because
 *      starting the referral sends that email straight away.
 *   Then "Important information": the service's statement for teachers and
 *      education staff, word for word from the frame; its button starts the
 *      referral.
 */
const STEPS = ['Consent to refer', "Child or Young Person's Details", 'What the referral is for', 'Nominated Parent or Carer'];
// The page after the four rail steps (not on the rail, as drawn).
const IMPORTANT_STEP = STEPS.length;

// Yes or No only (review query 08); "No" says so at once (3903:24725).
const CONSENT_OPTIONS = [
  { value: 'yes', label: 'Yes - appropriate consent has been given' },
  { value: 'no', label: 'No' },
];
const NO_CONSENT_LINE = "You must have the parent or carer's consent to proceed";

// What the referral is for, worded as the frame (3192:159477) with the
// pupil's name. The values are the intake concern the server records.
const reasonOptions = (firstName) => [
  { value: 'asd', label: `I think ${firstName} may have autism` },
  { value: 'adhd', label: `I think ${firstName} may have ADHD` },
  { value: 'both', label: `I think ${firstName} may have both autism and ADHD` },
];

// The requestForReferral guardian_told_to_expect_email question, word for word
// (section referral_details). It names no sender: the parent's email leads
// with the SENCO and the school (TDI-507, TDI-666).
const TOLD_TO_EXPECT_EMAIL_LABEL =
  "We need information of the child or young person's parent or carer to complete the forms. Have they been told to expect an email to provide further referral information?";
const TOLD_TO_EXPECT_EMAIL_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export default function HarbourAddToReferralFlyout({ pupil, orgId, onClose, onStarted }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [consent, setConsent] = useState(null);
  const [reason, setReason] = useState(null);
  // The GP practice question (TDI-488): asked for ADHD and combined referrals
  // only; one of the approved pilot practices, 'other' or 'unknown'.
  const [gpPractice, setGpPractice] = useState('');
  const [informantId, setInformantId] = useState(null);
  const [toldToExpectEmail, setToldToExpectEmail] = useState(null);
  const [toldError, setToldError] = useState(null);
  const [addingParent, setAddingParent] = useState(false);
  const [newParent, setNewParent] = useState({ fullName: '', relationship: '', email: '', phone: '' });
  const [newParentErrors, setNewParentErrors] = useState({});
  const [extraParents, setExtraParents] = useState([]);
  const [stepError, setStepError] = useState(null);
  const [busy, setBusy] = useState(false);
  // A parent already saved on the record is only removed after a confirm.
  const [confirmRemove, setConfirmRemove] = useState(null);
  // Step 2 "Change" links: edit the pupil's details or address in place
  // (rmsHarbourUpdatePupil — allowed while no referral is active yet).
  const [editing, setEditing] = useState(null); // null | 'details' | 'address'
  const [editValues, setEditValues] = useState({});
  const [editError, setEditError] = useState(null);
  const [editBusy, setEditBusy] = useState(false);

  const firstName = pupil ? pupil.name.split(' ')[0] : 'the pupil';
  const patient = pupil?.patient || {};
  const subject = patient.subject || {};
  const address = subject.address || {};

  // Pilot eligibility (TDI-488; SOP v0.3 section 8 and Appendix G). The year
  // group decides what the pilot can take; the rules run again on the server.
  // Interim wording pending Jasmine's copy.
  const yearGroup = effectiveYearGroup(pupil);
  const adhdYearCheck = evaluateYearGroup('adhd', yearGroup);
  const asdYearCheck = evaluateYearGroup('asd', yearGroup);
  const usualRoute = 'You can still refer through your usual route, with no delay or disadvantage.';
  // Why the chosen option cannot go ahead on year group alone, or null.
  const reasonBlock = (() => {
    if (!reason) return null;
    const adhdWanted = reason === 'adhd' || reason === 'both';
    const asdWanted = reason === 'asd' || reason === 'both';
    if (adhdWanted && !adhdYearCheck.eligible) {
      if (reason === 'both' && asdYearCheck.eligible) {
        return `ADHD referrals through this pilot are for pupils in Year 2 to Year 11, so a combined referral cannot be made for ${firstName}. You can refer for autism here, and for ADHD through your usual route, with no delay or disadvantage.`;
      }
      if (reason === 'both') {
        return `Referrals through this pilot are not available for ${firstName}'s year group. ${usualRoute}`;
      }
      return `ADHD referrals through this pilot are for pupils in Year 2 to Year 11, so an ADHD referral cannot be made here for ${firstName}. You can still refer for ADHD through your usual route, with no delay or disadvantage.`;
    }
    if (asdWanted && !asdYearCheck.eligible) {
      return `Autism referrals through this pilot are open until 31 December of Year 11, so ${reason === 'both' ? 'a combined' : 'an autism'} referral cannot be made here for ${firstName}. ${usualRoute}`;
    }
    return null;
  })();
  // The GP practice question appears once the chosen option needs it and the
  // year group allows it.
  const gpQuestionShown = Boolean(reason && (reason === 'adhd' || reason === 'both') && !reasonBlock);
  const gpBlock = (() => {
    if (!gpQuestionShown || !gpPractice) return null;
    if (gpPractice === GP_PRACTICE_UNKNOWN) {
      return `Check which GP practice ${firstName} is registered with, then come back to this. ADHD referrals through this pilot are for children registered with an approved pilot practice. You can still refer for ADHD through your usual route, with no delay or disadvantage.`;
    }
    if (gpPractice === GP_PRACTICE_OTHER) {
      if (reason === 'both') {
        return `${firstName} is not registered with an approved pilot GP practice, so a combined referral cannot be made here. You can refer for autism here, and for ADHD through your usual route, with no delay or disadvantage.`;
      }
      return `${firstName} is not registered with an approved pilot GP practice, so an ADHD referral cannot be made here. You can still refer for ADHD through your usual route, with no delay or disadvantage.`;
    }
    return null;
  })();

  const parentOptions = useMemo(() => {
    const onRecord = (pupil?.guardians || [])
      .filter((g) => g.status !== 'superseded')
      .map((g) => ({
        value: g.participantId || g.id,
        label: [g.firstName, g.lastName].filter(Boolean).join(' ') || g.email,
        meta: [relationshipLabel(g.relationship) || 'Parent', g.phone, g.email].filter(Boolean).join(' · '),
        removable: g.status !== 'account_linked',
      }));
    return [...onRecord, ...extraParents];
  }, [pupil, extraParents]);

  const selectedInformant = informantId || (parentOptions[0] && parentOptions[0].value) || null;

  const startEdit = (which) => {
    setEditError(null);
    if (which === 'details') {
      const dob = String(patient.dateOfBirth || subject.dob || '');
      const [y, m, d] = dob.split('-');
      setEditValues({
        firstName: patient.firstName || subject.firstName || '',
        lastName: patient.lastName || subject.lastName || '',
        dob: { day: d || '', month: m || '', year: y || '' },
        yearGroup: pupil?.yearGroup || patient.yearGroup || '',
      });
    } else {
      setEditValues({
        street: address.street || '',
        city: address.city || '',
        postcode: address.postcode || '',
      });
    }
    setEditing(which);
  };

  const saveEdit = async () => {
    setEditError(null);
    const payload = { orgId, patientRecordId: pupil.id };
    if (editing === 'details') {
      const iso = dateFieldToIso(editValues.dob || {});
      if (!iso) return setEditError('dob');
      payload.child = {
        firstName: (editValues.firstName || '').trim(),
        lastName: (editValues.lastName || '').trim(),
        dob: iso,
        yearGroup: editValues.yearGroup || null,
      };
    } else {
      payload.address = {
        street: (editValues.street || '').trim() || undefined,
        city: (editValues.city || '').trim() || undefined,
        postcode: (editValues.postcode || '').trim() || undefined,
      };
    }
    setEditBusy(true);
    try {
      await apiHarbourUpdatePupil(payload);
      if (onStarted) await onStarted(); // refresh the pupil data behind the wizard
      setEditing(null);
    } catch (e) {
      setEditError(e?.message || 'The details could not be saved.');
    } finally {
      setEditBusy(false);
    }
    return null;
  };

  const next = () => {
    setStepError(null);
    if (step === 0) {
      if (!consent) return setStepError('Answer the consent question to continue');
      if (consent !== 'yes') return null;
    }
    if (step === 1 && !yearGroup) {
      // The eligibility rules need the year group (TDI-488); Change opens the
      // details edit with the year group select right there.
      return setStepError(`Add ${firstName}'s year group using Change, so we can check what this pilot can take.`);
    }
    if (step === 2) {
      if (!reason) return setStepError('Choose what the referral is for');
      if (reasonBlock || gpBlock) return null; // the notice carries the reason
      if (gpQuestionShown && !gpPractice) {
        return setStepError(`Choose the GP practice ${firstName} is registered with`);
      }
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
    return null;
  };

  // Step 4's Continue: the nomination and the expect-an-email answer are
  // checked here, before the Important information page.
  const continueFromInformant = () => {
    setStepError(null);
    const chosen = parentOptions.find((p) => p.value === selectedInformant);
    if (!chosen) return setStepError('Nominate a responsible parent or carer');
    if (!toldToExpectEmail) {
      return setToldError('Answer whether the parent or carer has been told to expect an email');
    }
    if (toldToExpectEmail !== 'yes') {
      // Starting the referral emails the parent or carer straight away, so
      // they must have been told to expect it first: a stop, like consent.
      return setToldError(
        `Tell the parent or carer to expect an email asking them to fill in the referral forms before you add ${firstName} to the referral pathway. Come back to this once you have talked to them.`
      );
    }
    setStep(IMPORTANT_STEP);
    return null;
  };

  const saveNewParent = () => {
    const errs = {};
    const [first, ...rest] = newParent.fullName.trim().split(/\s+/);
    if (!first || rest.length === 0) errs.fullName = "Enter the parent or carer's full name";
    if (!EMAIL_RE.test(newParent.email.trim())) errs.email = "Enter the parent or carer's email address";
    setNewParentErrors(errs);
    if (Object.keys(errs).length) return;
    const value = `new-${Date.now()}`;
    setExtraParents((list) => [
      ...list,
      {
        value,
        label: newParent.fullName.trim(),
        meta: [relationshipLabel(newParent.relationship) || 'Parent', newParent.phone.trim(), newParent.email.trim()].filter(Boolean).join(' · '),
        payload: {
          firstName: first,
          lastName: rest.join(' '),
          email: newParent.email.trim(),
          phone: newParent.phone.trim() || undefined,
          relationship: newParent.relationship || 'parent',
        },
      },
    ]);
    setInformantId(value);
    setAddingParent(false);
    setNewParent({ fullName: '', relationship: '', email: '', phone: '' });
  };

  const removeParent = (option) => {
    setStepError(null);
    if (String(option.value).startsWith('new-')) {
      // Added in this wizard and not saved yet: nothing to undo.
      setExtraParents((list) => list.filter((p) => p.value !== option.value));
      if (informantId === option.value) setInformantId(null);
      return;
    }
    setConfirmRemove(option);
  };
  const confirmRemoveParent = async () => {
    const option = confirmRemove;
    if (!option) return;
    setBusy(true);
    try {
      await apiHarbourRemoveParent({ orgId, patientRecordId: pupil.id, participantId: option.value });
      if (informantId === option.value) setInformantId(null);
      setConfirmRemove(null);
      if (onStarted) await onStarted(); // refresh participants
    } catch (e) {
      setConfirmRemove(null);
      setStepError(e?.message || 'The parent or carer could not be removed.');
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    setStepError(null);
    if (reasonBlock || gpBlock || (gpQuestionShown && !gpPractice)) {
      // The pilot rules stop this referral (TDI-488): back to the reason step,
      // where the explanation lives.
      setStep(2);
      return null;
    }
    const chosen = parentOptions.find((p) => p.value === selectedInformant);
    if (!chosen || toldToExpectEmail !== 'yes') {
      setStep(3);
      return null;
    }
    setBusy(true);
    try {
      await apiHarbourStartReferral({
        orgId,
        patientRecordId: pupil.id,
        consentAnswer: consent,
        referralFor: reason,
        // Which consent wording this wizard showed (TDI-480).
        consentVersion: SCHOOL_REFERRAL_CONSENT_VERSION,
        gpPractice: gpQuestionShown ? gpPractice : undefined,
        informant: chosen.payload ? chosen.payload : { participantId: chosen.value },
        guardianToldToExpectEmail: toldToExpectEmail,
      });
      if (onStarted) await onStarted();
      navigate(`/rms/pupils/${pupil.id}`, {
        replace: true,
        state: { harbourAlert: `${firstName} added to the referral pathway` },
      });
    } catch (e) {
      setStepError(e?.message || 'That did not work. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
    return null;
  };

  const footer = (primaryLabel, onPrimary, { leadingIcon, trailingIcon, disabled = false } = {}) => (
    <div className="harbour-form-footer">
      <HarbourButton kind="tertiary" onClick={onClose}>Cancel</HarbourButton>
      {step > 0 && <HarbourButton kind="secondary" onClick={() => { setStepError(null); setStep((s) => s - 1); }}>Back</HarbourButton>}
      <HarbourButton kind="primary" leadingIcon={leadingIcon} trailingIcon={trailingIcon} busy={busy} disabled={disabled} onClick={onPrimary}>
        {primaryLabel}
      </HarbourButton>
    </div>
  );

  return (
    <HarbourFlyout
      width="wide"
      title={pupil?.name || ''}
      year={pupil?.yearGroup}
      eyebrow={{ tone: 'referral', label: 'Add to referral pathway' }}
      onClose={onClose}
    >
      <div className="harbour-task-body">
        <div className="harbour-task-body__steps">
          {step < IMPORTANT_STEP && <HarbourStepsRail tone="referral" steps={STEPS} currentIndex={step} />}
        </div>
        <div className="harbour-task-body__main">
          <div className="harbour-form-card">
            {step === 0 && (
              <>
                <h2 className="harbour-form-card__title">
                  {`Has consent to make this referral been given by a person who holds parental responsibility for ${firstName}?`}
                </h2>
                <p className="harbour-form-card__hint">
                  {`The referral should have been discussed with a person who holds parental responsibility for ${firstName} before their details are entered. Where possible and appropriate, involve ${firstName} in this discussion.`}
                </p>
                <HarbourOptionGroup
                  ariaLabel="Consent to refer"
                  tone="referral"
                  options={CONSENT_OPTIONS}
                  value={consent}
                  onChange={(v) => { setConsent(v); setStepError(null); }}
                />
                {consent === 'no' && <HarbourNotice kind="warning" title={NO_CONSENT_LINE} />}
                {stepError && (
                  <HarbourNotice kind="error" title="Consent needed">
                    {stepError}
                  </HarbourNotice>
                )}
                {footer('Continue', next, { trailingIcon: 'arrow-right', disabled: consent === 'no' })}
              </>
            )}
            {step === 1 && !editing && (
              <>
                <h2 className="harbour-form-card__title">Child or Young Person&apos;s Details</h2>
                <div className="flex flex-col divide-y divide-[color:var(--harbour-colour-line-default)]">
                  <div className="flex items-start gap-8 py-4 flex-wrap">
                    <SummaryFact label="Name" value={pupil?.name} />
                    <SummaryFact label="Date of birth" value={formatDob(patient.dateOfBirth || subject.dob)} />
                    <SummaryFact label="Year group" value={pupil?.yearGroup} />
                    <span className="ml-auto flex-none">
                      <HarbourTextLink onClick={() => startEdit('details')}>Change</HarbourTextLink>
                    </span>
                  </div>
                  <div className="flex items-start gap-8 py-4">
                    <SummaryFact
                      label="Home address"
                      value={[address.street, address.city, address.postcode].filter(Boolean).join(', ') || 'Not recorded'}
                    />
                    <span className="ml-auto flex-none">
                      <HarbourTextLink onClick={() => startEdit('address')}>Change</HarbourTextLink>
                    </span>
                  </div>
                </div>
                {stepError && <HarbourNotice kind="error" title="Year group needed">{stepError}</HarbourNotice>}
                {footer('Continue', next, { trailingIcon: 'arrow-right' })}
              </>
            )}
            {step === 1 && editing === 'details' && (
              <>
                <h2 className="harbour-form-card__title">Child or Young Person&apos;s Details</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <HarbourField label="First name" value={editValues.firstName || ''} onChange={(e) => setEditValues((v) => ({ ...v, firstName: e.target.value }))} />
                  <HarbourField label="Last name" value={editValues.lastName || ''} onChange={(e) => setEditValues((v) => ({ ...v, lastName: e.target.value }))} />
                </div>
                <HarbourDateField label="Date of birth" value={editValues.dob || {}} onChange={(dob) => setEditValues((v) => ({ ...v, dob }))} error={editError === 'dob' ? "Enter the pupil's date of birth" : null} />
                <HarbourField as="select" label="Year group" value={editValues.yearGroup || ''} onChange={(e) => setEditValues((v) => ({ ...v, yearGroup: e.target.value }))} className="md:max-w-[344px]">
                  <option value="">Choose the pupil&apos;s year group</option>
                  {YEAR_GROUPS.map((yg) => <option key={yg} value={yg}>{yg}</option>)}
                </HarbourField>
                {editError && editError !== 'dob' && <HarbourNotice kind="error" title="That did not work">{editError}</HarbourNotice>}
                <div className="harbour-form-footer">
                  <HarbourButton kind="tertiary" onClick={() => { setEditing(null); setEditError(null); }}>Cancel</HarbourButton>
                  <HarbourButton kind="primary" busy={editBusy} onClick={saveEdit}>Save</HarbourButton>
                </div>
              </>
            )}
            {step === 1 && editing === 'address' && (
              <>
                <h2 className="harbour-form-card__title">Home address</h2>
                <HarbourField label="Street Address" value={editValues.street || ''} onChange={(e) => setEditValues((v) => ({ ...v, street: e.target.value }))} />
                <div className="grid grid-cols-1 md:grid-cols-[1fr_200px] gap-4">
                  <HarbourField label="City or Town" value={editValues.city || ''} onChange={(e) => setEditValues((v) => ({ ...v, city: e.target.value }))} />
                  <HarbourField label="Postcode" value={editValues.postcode || ''} onChange={(e) => setEditValues((v) => ({ ...v, postcode: e.target.value }))} />
                </div>
                {editError && <HarbourNotice kind="error" title="That did not work">{editError}</HarbourNotice>}
                <div className="harbour-form-footer">
                  <HarbourButton kind="tertiary" onClick={() => { setEditing(null); setEditError(null); }}>Cancel</HarbourButton>
                  <HarbourButton kind="primary" busy={editBusy} onClick={saveEdit}>Save</HarbourButton>
                </div>
              </>
            )}
            {step === 2 && (
              <>
                <h2 className="harbour-form-card__title">What is the main reason you are making this referral?</h2>
                <HarbourOptionGroup
                  ariaLabel="What the referral is for"
                  tone="referral"
                  options={reasonOptions(firstName)}
                  value={reason}
                  onChange={(v) => { setReason(v); setStepError(null); }}
                />
                {reasonBlock && (
                  <HarbourNotice kind="warning" title="Not available through this pilot">
                    {reasonBlock}
                  </HarbourNotice>
                )}
                {gpQuestionShown && (
                  <div className="flex flex-col gap-3">
                    <HarbourField
                      as="select"
                      label={`Which GP practice is ${firstName} registered with?`}
                      hint="ADHD referrals through this pilot are for children registered with one of these approved practices."
                      value={gpPractice}
                      onChange={(e) => { setGpPractice(e.target.value); setStepError(null); }}
                      className="md:max-w-[344px]"
                    >
                      <option value="">Choose a GP practice</option>
                      {APPROVED_GP_PRACTICES.map((p) => <option key={p} value={p}>{p}</option>)}
                      <option value={GP_PRACTICE_OTHER}>Another practice</option>
                      <option value={GP_PRACTICE_UNKNOWN}>I don&apos;t know yet</option>
                    </HarbourField>
                    {gpBlock && (
                      <HarbourNotice
                        kind="warning"
                        title={gpPractice === GP_PRACTICE_UNKNOWN ? 'Check the GP practice first' : 'Not available through this pilot'}
                      >
                        {gpBlock}
                      </HarbourNotice>
                    )}
                  </div>
                )}
                {stepError && <HarbourNotice kind="error" title="Choose an option">{stepError}</HarbourNotice>}
                {footer('Continue', next, { trailingIcon: 'arrow-right', disabled: Boolean(reasonBlock || gpBlock) })}
              </>
            )}
            {step === 3 && !addingParent && (
              <>
                <h2 className="harbour-form-card__title">Nominate a Parent / Carer</h2>
                <p className="harbour-form-card__hint">
                  Please provide the details of a parent or carer with legal responsibility for the child:
                </p>
                <HarbourOptionGroup
                  ariaLabel="Responsible parent or carer"
                  tone="referral"
                  options={parentOptions.map((p) => ({
                    ...p,
                    trailing: p.removable !== false && parentOptions.length > 1 ? (
                      <HarbourTextLink
                        onClick={(e) => { e.stopPropagation(); removeParent(p); }}
                      >
                        Remove
                      </HarbourTextLink>
                    ) : null,
                  }))}
                  value={selectedInformant}
                  onChange={setInformantId}
                />
                <div>
                  <HarbourButton kind="secondary" leadingIcon="plus" onClick={() => setAddingParent(true)}>
                    Add a parent / carer
                  </HarbourButton>
                </div>
                <div className="flex flex-col gap-3">
                  <h3 className="harbour-body-16-bold">{TOLD_TO_EXPECT_EMAIL_LABEL}</h3>
                  <HarbourOptionGroup
                    ariaLabel="Parent or carer told to expect an email"
                    tone="referral"
                    options={TOLD_TO_EXPECT_EMAIL_OPTIONS}
                    value={toldToExpectEmail}
                    onChange={(v) => { setToldToExpectEmail(v); setToldError(null); }}
                  />
                  {toldError && (
                    <HarbourNotice kind={toldToExpectEmail === 'no' ? 'warning' : 'error'}>
                      {toldError}
                    </HarbourNotice>
                  )}
                </div>
                {stepError && <HarbourNotice kind="error" title="That did not work">{stepError}</HarbourNotice>}
                {footer('Continue', continueFromInformant, { trailingIcon: 'arrow-right' })}
              </>
            )}
            {step === IMPORTANT_STEP && (
              <>
                <h2 className="harbour-form-card__title">Important information</h2>
                <HarbourSchoolStaffStatement />
                {stepError && <HarbourNotice kind="error" title="That did not work">{stepError}</HarbourNotice>}
                <div className="harbour-form-footer">
                  <HarbourButton kind="secondary" onClick={() => { setStepError(null); setStep(3); }}>Back</HarbourButton>
                  <HarbourButton kind="primary" busy={busy} onClick={submit}>Add to referral pathway</HarbourButton>
                </div>
              </>
            )}
            {step === 3 && addingParent && (
              <>
                <h2 className="harbour-form-card__title">Add Parent / Carer</h2>
                <HarbourField label="Full name" value={newParent.fullName} onChange={(e) => setNewParent((p) => ({ ...p, fullName: e.target.value }))} error={newParentErrors.fullName} />
                <HarbourField as="select" label={`Relationship to ${firstName}`} value={newParent.relationship} onChange={(e) => setNewParent((p) => ({ ...p, relationship: e.target.value }))}>
                  <option value="" />
                  <option value="parent">Parent</option>
                  <option value="step_parent">Step-parent</option>
                  <option value="foster_carer">Foster carer</option>
                  <option value="kinship_carer">Kinship carer</option>
                  <option value="legal_guardian">Legal guardian</option>
                </HarbourField>
                <HarbourField
                  label="Email address"
                  hint="We send the sign in email to this address"
                  type="email"
                  value={newParent.email}
                  onChange={(e) => setNewParent((p) => ({ ...p, email: e.target.value }))}
                  error={newParentErrors.email}
                />
                <HarbourField label="Phone number" type="tel" value={newParent.phone} onChange={(e) => setNewParent((p) => ({ ...p, phone: e.target.value }))} />
                <div className="harbour-form-footer">
                  <HarbourButton kind="tertiary" onClick={() => setAddingParent(false)}>Cancel</HarbourButton>
                  <HarbourButton kind="primary" onClick={saveNewParent}>Save</HarbourButton>
                </div>
              </>
            )}
          </div>
        </div>
        <aside className="harbour-task-body__aside" />
      </div>
      {confirmRemove && (
        <HarbourModal
          title={`Remove ${confirmRemove.label}?`}
          confirmLabel="Remove"
          busy={busy}
          onConfirm={confirmRemoveParent}
          onClose={() => setConfirmRemove(null)}
        >
          <p className="harbour-body-16">{`They will no longer be listed on ${firstName}'s record.`}</p>
        </HarbourModal>
      )}
    </HarbourFlyout>
  );
}

function SummaryFact({ label, value }) {
  return (
    <div className="flex flex-col gap-0.5 min-w-0">
      <span className="harbour-body-16 text-[color:var(--harbour-colour-text-muted)]">{label}</span>
      <span className="harbour-body-16-bold">{value || '—'}</span>
    </div>
  );
}

function relationshipLabel(id) {
  const labels = {
    parent: 'Parent',
    step_parent: 'Step-parent',
    foster_carer: 'Foster carer',
    kinship_carer: 'Kinship carer',
    legal_guardian: 'Legal guardian',
    informal_carer: 'Informal carer',
  };
  return labels[id] || null;
}

function formatDob(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}
